import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { pathPlayerView } from "../../../../config/routes";
import { PlayerMedium } from "../../../../client/api";
import RoundedRectangle from "../../../components/RoundedRectangle";
import PlayerImage from "../../../components/PlayerImage";
import ScaleFade from "../../../components/Animated/ScaleFade";
import { TranslatorKeys } from "../../../../config/translator";
import ProBanner from "./ProBanner";

export type PlayerStatData = {
  player: PlayerMedium;
  value: number;
  placement: number;
};

type PlayerStatisticItemProps = PlayerStatData & {
  unit?: string;
  formatAsDecimal?: boolean;
};

const PlayerStatisticItem: React.FC<PlayerStatisticItemProps> = ({
  player,
  value,
  placement,
  unit = "",
  formatAsDecimal,
}) => {
  const navigate = useNavigate();

  const handlePlayerClick = () => {
    if (player.id) {
      navigate(pathPlayerView(player.id));
    }
  };

  const formattedValue = formatAsDecimal ? value.toFixed(1) : value.toString();

  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-shrink-0 w-6 text-center">
        <span className="text-sm font-semibold text-font-600 dark:text-font-600-dark">
          {placement}
        </span>
      </div>
      <div className="flex-shrink-0">
        <ScaleFade isOpen hoverOnContainer className="w-10 h-10">
          <button
            type="button"
            onClick={handlePlayerClick}
            className="w-10 h-10 overflow-hidden rounded-full cursor-pointer"
          >
            <PlayerImage
              image={player.photo ?? undefined}
              width={40}
              className="rounded-full"
              round
            />
          </button>
        </ScaleFade>
      </div>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={handlePlayerClick}
          className="text-left w-full hover:text-primary dark:hover:text-primary transition-colors"
        >
          <div className="text-sm font-medium text-font dark:text-font-dark truncate">
            {player.familyName}
          </div>
          <div className="text-xs text-font-600 dark:text-font-600-dark truncate">
            {player.givenName}
          </div>
        </button>
      </div>
      <div className="flex-shrink-0">
        <span className="text-sm font-semibold text-font dark:text-font-dark">
          {formattedValue}
          {unit && <span className="text-xs ml-1">{unit}</span>}
        </span>
      </div>
    </div>
  );
};

const createPlaceholderPlayer = (
  familyName: string,
  placement: number,
  value: number
): PlayerStatData => ({
  player: {
    id: 0,
    givenName: "Player",
    familyName,
    photo: null,
    insiderCanModifyImage: false,
    jersey: null,
    foot: null,
    height: null,
    rating: null,
    mainTeam: null,
    mainPosition: null,
    birthdate: null,
  },
  value,
  placement,
});

const PLACEHOLDER_PLAYERS_DECIMAL: PlayerStatData[] = [
  createPlaceholderPlayer("One", 1, 8.4),
  createPlaceholderPlayer("Two", 2, 8.2),
  createPlaceholderPlayer("Three", 3, 8.0),
];

const PLACEHOLDER_PLAYERS_INTEGER: PlayerStatData[] = [
  createPlaceholderPlayer("One", 1, 15),
  createPlaceholderPlayer("Two", 2, 12),
  createPlaceholderPlayer("Three", 3, 10),
];

const PREMIUM_FEATURES: TranslatorKeys[] = [
  "evaluation",
  "mostAssists", 
  "mostYellowCards",
  "mostRedCards",
  "mostTotwAwards",
  "mostMotmAwards",
];

type PlayerStatisticPanelProps = {
  titleKey: TranslatorKeys;
  players: PlayerStatData[];
  unit?: string;
  formatAsDecimal?: boolean;
  shouldBlur?: boolean;
  showBanner?: boolean;
};

const PlayerStatisticPanel: React.FC<PlayerStatisticPanelProps> = ({
  titleKey,
  players,
  unit,
  formatAsDecimal,
  shouldBlur = false,
  showBanner = true,
}) => {
  const displayPlayers = useMemo(() => {
    if (players.length > 0) return players;
    if (!shouldBlur) return [];
    
    return formatAsDecimal ? PLACEHOLDER_PLAYERS_DECIMAL : PLACEHOLDER_PLAYERS_INTEGER;
  }, [players, shouldBlur, formatAsDecimal]);

  if (displayPlayers.length === 0) {
    return null;
  }

  const showPremiumBanner = shouldBlur && showBanner;

  return (
    <RoundedRectangle
      className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark relative"
      hasBorder
      hasTitle={!shouldBlur}
      titleType="upLeft"
      titleKey={shouldBlur ? undefined : titleKey}
    >
      <div
        className={`px-4 pt-4 ${
          shouldBlur ? "pb-24 blur-sm" : "pb-4"
        }`}
      >
        {displayPlayers.map((item, index) => (
          <PlayerStatisticItem
            key={item.player.id || `placeholder-${index}`}
            player={item.player}
            value={item.value}
            placement={item.placement}
            unit={unit}
            formatAsDecimal={formatAsDecimal}
          />
        ))}
      </div>
      {showPremiumBanner && <ProBanner featureList={PREMIUM_FEATURES} />}
    </RoundedRectangle>
  );
};

export default PlayerStatisticPanel;

